// Molecular: Data validation and coverage analysis service
// NOTE: This service is deprecated due to loadEnergyData causing browser crashes
// It will return minimal data to prevent crashes

import { EnergyType } from '../types';
import { loadEnergyData, loadGemeindenData } from './dataService';

interface DataCoverageReport {
  gemeinden: {
    total: number;
    withEnergyData: number;
    coverage: number;
  };
  energySources: {
    [K in EnergyType]: {
      installations: number;
      coverage: number;
      plzCovered: string[];
    };
  };
  missingData: {
    gemeindenWithoutEnergy: string[];
    plzWithoutData: string[];
  };
  lastUpdated: string;
}

interface DataStats {
  totalInstallations: number;
  totalPower: number;
  avgPowerPerInstallation: number;
  mostCommonTechnology: string;
}

/**
 * Validate and analyze data coverage across all municipalities
 * @deprecated This function is disabled to prevent browser crashes from loading large energy files
 */
export async function validateDataCoverage(): Promise<DataCoverageReport> {
  try {
    
    // Load only gemeente data (energy data loading disabled to prevent browser crashes)
    const gemeindenData = await loadGemeindenData();
    
    // Return minimal data to prevent crashes
    const energyDataSources = {
      wasser: {},
      sonne: {},
      biogas: {},
      biomasse: {}
    };

    // Create a set of all PLZ from gemeinden data
    const allPlz = new Set<string>();
    const allGemeinden = gemeindenData.gemeinden || [];
    
    allGemeinden.forEach(gemeinde => {
      gemeinde.plzList?.forEach(plz => allPlz.add(plz));
    });

    // Analyze coverage for each energy source
    const energySources: DataCoverageReport['energySources'] = {} as DataCoverageReport['energySources'];
    const coveredPlz = new Set<string>();

    for (const [type, data] of Object.entries(energyDataSources)) {
      const plzCovered = Object.keys(data);
      const installations = Object.values(data).flat().length;
      
      energySources[type as EnergyType] = {
        installations,
        coverage: Math.round((plzCovered.length / allPlz.size) * 100 * 100) / 100,
        plzCovered
      };

      // Add to overall covered PLZ
      plzCovered.forEach(plz => coveredPlz.add(plz));
    }

    // Find gemeinden with energy data
    const gemeindenWithEnergy = allGemeinden.filter(gemeinde => 
      gemeinde.plzList?.some(plz => coveredPlz.has(plz))
    );

    // Find missing data
    const gemeindenWithoutEnergy = allGemeinden
      .filter(gemeinde => !gemeinde.plzList?.some(plz => coveredPlz.has(plz)))
      .map(g => g.name);

    const plzWithoutData = Array.from(allPlz).filter(plz => !coveredPlz.has(plz));

    const report: DataCoverageReport = {
      gemeinden: {
        total: allGemeinden.length,
        withEnergyData: gemeindenWithEnergy.length,
        coverage: Math.round((gemeindenWithEnergy.length / allGemeinden.length) * 100 * 100) / 100
      },
      energySources,
      missingData: {
        gemeindenWithoutEnergy: gemeindenWithoutEnergy.slice(0, 10), // Limit to first 10
        plzWithoutData: plzWithoutData.slice(0, 20) // Limit to first 20
      },
      lastUpdated: new Date().toISOString()
    };


    return report;
  } catch (error) {
    console.error('[DataValidation] Error during coverage analysis:', error);
    throw error;
  }
}

/**
 * Get detailed statistics for a specific energy type
 * @deprecated This function is disabled to prevent browser crashes from loading large energy files
 */
export async function getEnergySourceStats(type: EnergyType): Promise<DataStats> {
  console.warn(`[DataValidation] getEnergySourceStats(${type}) is disabled to prevent browser crashes`);
  
  // Return minimal data to prevent crashes
  return {
    totalInstallations: 0,
    totalPower: 0,
    avgPowerPerInstallation: 0,
    mostCommonTechnology: 'N/A'
  };
}

/**
 * Check if data needs updating (based on cache age and completeness)
 */
export async function shouldUpdateData(): Promise<boolean> {
  try {
    const coverage = await validateDataCoverage();
    
    // Update if coverage is below 80% or data is older than 7 days
    const dataAge = Date.now() - new Date(coverage.lastUpdated).getTime();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    
    return coverage.gemeinden.coverage < 80 || dataAge > sevenDays;
  } catch (error) {
    console.error('Error checking if data needs update:', error);
    return true; // Default to update if we can't determine
  }
}

/**
 * Log data coverage summary to console
 */
export async function logDataCoverage(): Promise<void> {
  try {
    const report = await validateDataCoverage();
    
    console.group('📊 E-Control Data Coverage Report');
    console.log(`🏘️  Municipalities: ${report.gemeinden.withEnergyData}/${report.gemeinden.total} (${report.gemeinden.coverage}%)`);
    
    Object.entries(report.energySources).forEach(([type, stats]) => {
      const icon = type === 'sonne' ? '☀️' : type === 'wasser' ? '💧' : type === 'biogas' ? '🌱' : '🌿';
      console.log(`${icon} ${type}: ${stats.installations} installations (${stats.coverage}% PLZ coverage)`);
    });
    
    if (report.missingData.gemeindenWithoutEnergy.length > 0) {
      console.warn('⚠️  Municipalities without energy data:', report.missingData.gemeindenWithoutEnergy.join(', '));
    }
    
    console.log(`📅 Last updated: ${new Date(report.lastUpdated).toLocaleString()}`);
    console.groupEnd();
  } catch (error) {
    console.error('Error logging data coverage:', error);
  }
}